// CLIENT-SIDE ERROR TELEMETRY.
//
// Why this exists: on 2026-08-18 the SPA white-paged for an ESGWND user during Sarah chat
// testing. Every backend run completed cleanly, and nothing anywhere recorded what the browser
// threw — the root cause was unverifiable. This module is the missing flight recorder: every
// uncaught render error (via the ErrorBoundary), uncaught exception, and unhandled promise
// rejection is appended to the `clientErrors` collection, where firestore.rules make it
// create-only from clients (reports may carry stack/PII fragments; nothing client-side reads
// them back — inspect via console/Admin SDK).
//
// Design rules, in the spirit of writeGuard.js:
//  - Dependency-free of React; the boundary and window hooks both call reportClientError().
//  - Telemetry must NEVER make things worse: a failed report logs to console and stops —
//    no toast (that's writeGuard's job for user-initiated writes), no retry, no rethrow.
//  - Rate-limited: a crash inside a render/snapshot loop fires the same error hundreds of
//    times a second; the burst window plus the session cap keep that to a handful of writes.

import { addDoc, collection, serverTimestamp } from 'firebase/firestore'
import { db, auth } from './firebase'

// Identity is injected by AuthContext (the only place that knows the tenant claim) so this
// module never imports React or context. `domain` is the caller's OWN tenant (claimOrg) —
// firestore.rules require the created doc to carry it; `viewingDomain` records which tenant a
// platform admin was looking at when it differs.
let identity = { domain: null, viewingDomain: null }
export const setClientErrorIdentity = (next) => { identity = { ...identity, ...next } }

// --- Rate limiting -------------------------------------------------------------------------
// Burst window: at most BURST_MAX writes per WINDOW_MS. Session cap: at most SESSION_MAX
// writes per page load, ever — after that we only count. Duplicate messages inside the window
// are folded into the previous report (a render loop is one bug, not three hundred).
const WINDOW_MS = 60_000
const BURST_MAX = 6
const SESSION_MAX = 25
let windowStart = 0
let windowCount = 0
let sessionCount = 0
let suppressed = 0
let lastMessage = null
let lastMessageAt = 0

const allow = (message) => {
  const now = Date.now()
  if (message === lastMessage && now - lastMessageAt < 5_000) return false
  if (now - windowStart > WINDOW_MS) { windowStart = now; windowCount = 0 }
  if (windowCount >= BURST_MAX || sessionCount >= SESSION_MAX) return false
  windowCount += 1; sessionCount += 1
  lastMessage = message; lastMessageAt = now
  return true
}

// --- Reporting -----------------------------------------------------------------------------
let reporting = false // an error thrown while reporting must not report itself

/**
 * Append one client error to the `clientErrors` collection. Fire-and-forget by design —
 * callers are crash paths and must never await or depend on telemetry.
 *
 * @param {unknown} error   the thrown value (Error or otherwise)
 * @param {object}  extra   { componentStack, source } — source is 'boundary' | 'window.onerror'
 *                          | 'unhandledrejection'
 */
export function reportClientError(error, { componentStack = null, source = 'unknown' } = {}) {
  if (reporting) return
  const message = String(error?.message || error || 'Unknown error').slice(0, 2000)
  if (!allow(message)) { suppressed += 1; return }
  reporting = true
  try {
    // Rules require the report to carry the caller's own tenant. AuthContext injects it once
    // claims resolve; for crashes BEFORE that (boot, provider setup) read the claim off the
    // token directly, else the write would be rules-denied and the earliest crashes lost.
    const domainP = identity.domain
      ? Promise.resolve(identity.domain)
      : (auth.currentUser
          ? auth.currentUser.getIdTokenResult().then((t) => t.claims.organizationDomain || null).catch(() => null)
          : Promise.resolve(null))
    domainP.then((domain) => addDoc(collection(db, 'clientErrors'), {
      organizationDomain: domain,
      ...(identity.viewingDomain && identity.viewingDomain !== domain
        ? { viewingDomain: identity.viewingDomain } : {}),
      uid: auth.currentUser?.uid || null,
      route: window.location.pathname + window.location.search,
      message,
      stack: String(error?.stack || '').slice(0, 6000) || null,
      componentStack: componentStack ? String(componentStack).slice(0, 6000) : null,
      source,
      userAgent: navigator.userAgent,
      // How many earlier reports this session were rate-limit-suppressed before this one —
      // distinguishes "crashed once" from "crashed in a loop" when reading reports.
      suppressedBefore: suppressed,
      createdAt: serverTimestamp(),
    })).catch((e) => console.error('[clientErrors] report failed:', e?.code || '', e?.message || e))   // quiet-by-design: error telemetry only ever logs (ui.md)
  } catch (e) {   // quiet-by-design: error telemetry only ever logs (ui.md)
    console.error('[clientErrors] report failed:', e?.message || e)
  } finally {
    reporting = false
  }
}

/**
 * Install the window-level hooks. Call ONCE from main.jsx, before the app renders, so errors
 * during provider/auth bootstrap (which no React boundary can catch) are captured too.
 */
export function installClientErrorHandlers() {
  window.addEventListener('error', (event) => {
    // Resource-load failures (img/script tags) also fire 'error' but carry no Error object and
    // don't crash the app — skip them, they'd drown the signal.
    if (!event.error && !event.message) return
    reportClientError(event.error || event.message, { source: 'window.onerror' })
  })
  window.addEventListener('unhandledrejection', (event) => {
    reportClientError(event.reason, { source: 'unhandledrejection' })
  })
}
